import { useMutation, useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import React, { useEffect, useState, useRef, useId, useContext } from 'react';
import toast from 'react-hot-toast';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage.tsx';
import { Modal, ModalTitle, MODAL_OUTER_PADDING, MODAL_INNER_GAP } from 'design-system/src/components/Modal';
import Hairline from 'design-system/src/components/Hairline/Hairline.tsx';
import { Patient, client } from '../../api.tsx';

import { Controller, useForm } from 'react-hook-form';

import { useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow from 'design-system/src/components/ButtonRow/ButtonRow';
import { FormLabel } from '../FormLabel.tsx';
import Typeahead, { TypeaheadOption } from '../Typeahead.tsx';
import { Dropdown, DropdownItem, DropdownContainer } from '../Dropdown.tsx';
import { formatStandardDate } from '../../utils.tsx';
import { AxiosError } from 'axios';
import { InboundTaskType, NewCommentFile, FormFilesDocumentTypes } from '../../api.tsx';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner.tsx';
import { ExtraInfoTable } from '../ExtraInfoTable/ExtraInfoTable.tsx';
import CommentAndFileSection from './CommentAndFileSection.tsx';
import { AuthContext } from '../../auth.tsx';
import ExtendedNewTaskForm from './ExtendedNewTaskForm.tsx';
import { createTrackingProps } from 'shared-utils/analytics.ts';

export type TaskFormData = {
  text: string;
  patientId: string;
  prescriptionId: string;
  type?: InboundTaskType;
};

export type RxData = {
  drug: string;
  drug_name: string;
  id: string;
  prescriber_first_name: string;
  prescriber_last_name: string;
  written_date: string;
};

export const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${MODAL_INNER_GAP};
`;

// custom ModalBody because the footer buttons are inside the form and can't easily be split out into the ModalFooter component
export const CustomModalBody = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex-grow: 1;
  padding: ${MODAL_INNER_GAP} ${MODAL_OUTER_PADDING} ${MODAL_OUTER_PADDING};
  gap: ${MODAL_INNER_GAP};
`;

export const StyledExtraInfoTable = styled(ExtraInfoTable)`
  // the FormContainer gap provides the spacing we need
  margin: 0;
`;

export const StyledHairline = styled(Hairline)`
  // the FormContainer gap provides the spacing we need
  margin: 0;
`;

export const CancelButton = styled(Button)`
  border: 1px solid var(--border-gray);
  background: transparent;
  transition: background-color 0.2s ease-out;
  color: var(--black);

  &:hover {
    background: var(--light-border-gray);
  }
`;

export const LoadingForm = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

// Using <a> instead of NavLink because the Toast exists outside
// of the router system
// TODO(design-system): Replace with Link component
export const SuccessLink = styled.a`
  color: var(--dark-green);
  font-weight: 500;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: auto;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
`;

export const createPatientOption = (patient: Patient): TypeaheadOption => {
  const option: TypeaheadOption = {
    value: patient.id,
    description: `${patient.first_name} ${patient.last_name} (${formatStandardDate(patient.date_of_birth)})`,
  };
  return option;
};

export const createRxOption = (
  rxData: RxData,
): {
  description: string;
  value: string;
} => {
  const { id, drug, prescriber_first_name, prescriber_last_name, written_date } = rxData;
  const date = formatStandardDate(written_date);

  const option: {
    description: string;
    value: string;
  } = {
    value: id,
    description: `${drug}, ${prescriber_first_name} ${prescriber_last_name} on ${date}`,
  };

  return option;
};

export const basicTaskTypeOptions: { value: string; label: InboundTaskType }[] = [
  { value: 'authorization_update', label: 'Authorization update' },
  { value: 'pap_bridge_update', label: 'PAP/Bridge update' },
  { value: 'pharmacy', label: 'Pharmacy' },
  { value: 'pa_renewal', label: 'PA renewal' },
  { value: 'other', label: 'Other' },
];

export const NewTaskForm: React.FC<{
  onSubmit: (taskId: string) => void;
}> = (props) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [searchParams] = useSearchParams();
  const params = useParams();
  const patientLabelId = useId();
  const rxLabelId = useId();
  const taskTypeLabelId = useId();
  const taskCommentLabelId = useId();

  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  const submit = useMutation<{ task_id: string }, AxiosError<{ error: string }>, TaskFormData>({
    mutationFn: async (data) => {
      // TODO: share this logic with NewFollowupTaskModal.tsx or migrate that modal to use this component
      if (isTrainingMode) {
        console.warn('Training Mode: Mocking task creation, no real request sent.');

        // Simulating a successful response without sending the request
        return Promise.resolve({ task_id: 'mock-task-id' });
      }

      const formData = new FormData();
      formData.append('patient_id', data.patientId);
      formData.append('prescription_id', data.prescriptionId);
      formData.append('text', inputValue);
      formData.append('task_source', searchParams.get('source') || '');

      if (!data.type) {
        throw new Error('Task type is required');
      }
      formData.append('type', data.type);

      if (newFilesUploaded.length) {
        const formFilesDocumentTypes: FormFilesDocumentTypes = {};

        newFilesUploaded.forEach((fileInfo) => {
          formData.append('files[]', fileInfo.file);

          // We can't add the document type to the file directly,
          // so we track the types in a separate object
          // and use the file names as keys.
          formFilesDocumentTypes[fileInfo.file.name] = fileInfo.document_type;
        });

        formData.append('form_files_document_types', JSON.stringify(formFilesDocumentTypes));
      }

      return client.post(`tasks`, formData).then((res) => res.data);
    },
    onSuccess: ({ task_id }) => {
      props.onSubmit(task_id);
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      Sentry.captureException(error);
      console.error(error);
    },
  });
  const { handleSubmit, control, formState } = useForm<TaskFormData>({
    defaultValues: {
      patientId: searchParams.get('patientId') || '',
      prescriptionId: params.rxId || '',
    },
  });
  const [isFetchingRxList, setIsFetchingRxList] = useState<boolean>(false);
  const [isFetchingPatients, setIsFetchingPatients] = useState<boolean>(true);
  const [rxList, setRxList] = useState<{ value: string; description: string }[]>([]);
  const [patients, setPatients] = useState([]);
  const [newFilesUploaded, setNewFilesUploaded] = useState<NewCommentFile[]>([]);
  const [preselectedPatient, setPreselectedPatient] = useState<TypeaheadOption | null>(null);
  const [preselectedRx, setPreselectedRx] = useState<TypeaheadOption | null>(null);
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ['patients'],
    queryFn: () => {
      return client.get(`patients`).then((res) => res.data);
    },
  });

  const getRxList = useMutation<{ rxs: RxData[] }, AxiosError<{ error: string }>, { patientId: string }>({
    mutationFn: async (data) => {
      return client.get(`rx_list/${data.patientId}`).then((res) => res.data);
    },
    onSuccess: async (res_data) => {
      setRxList(res_data.rxs.map((rxData: RxData) => createRxOption(rxData)));
      setIsFetchingRxList(false);
      const preselectedRx = res_data.rxs.find((rxData: RxData) => rxData.id === params.rxId);
      if (preselectedRx) {
        setPreselectedRx(createRxOption(preselectedRx));
      }
    },
    onError: async () => {
      toast.custom(() => <StandardErrorToast />);
      setIsFetchingRxList(false);
    },
  });

  useEffect(() => {
    if (!query.data || !query.data.patients) {
      return;
    }
    setPatients(query.data.patients.map((patient: Patient) => createPatientOption(patient)));
    setIsFetchingPatients(false);
    const preselectedPatient = query.data.patients.find(
      (patient: Patient) => patient.id === searchParams.get('patientId'),
    );
    if (preselectedPatient) {
      setIsFetchingRxList(true);
      setPreselectedPatient(createPatientOption(preselectedPatient));
      getRxList.mutate({ patientId: preselectedPatient.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data, searchParams]);

  const handlePatientChange = (onChange: (patientId: string) => void, patientId: string) => {
    if (patientId) {
      onChange(patientId);
      setIsFetchingRxList(true);
      getRxList.mutate({ patientId: patientId });
    } else {
      setRxList([]);
    }
  };

  const isLoadingPreselectedRx = params.rxId && isFetchingRxList;
  const isLoadingPreselectedPatient = searchParams.get('patientId') && isFetchingPatients;

  if (isLoadingPreselectedRx || isLoadingPreselectedPatient) {
    return (
      <LoadingForm>
        <LoadingSpinner />
      </LoadingForm>
    );
  }

  const extraInfoData = [];
  if (preselectedPatient) {
    extraInfoData.push({ label: 'Patient', value: preselectedPatient.description });
  }
  if (preselectedRx) {
    extraInfoData.push({ label: 'Prescription', value: preselectedRx.description });
  }

  return (
    <FormContainer onSubmit={handleSubmit((data) => submit.mutate(data))}>
      {!!extraInfoData.length && <StyledExtraInfoTable data={extraInfoData} hairlineComponent={<StyledHairline />} />}
      {!preselectedPatient && (
        <Controller
          name="patientId"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <DropdownContainer>
              <FormLabel id={patientLabelId}>Patient</FormLabel>
              <Typeahead
                labelId={patientLabelId}
                options={patients}
                onChange={(patientId: string) => {
                  handlePatientChange(field.onChange, patientId);
                }}
              />
            </DropdownContainer>
          )}
        />
      )}
      {!preselectedRx && (
        <Controller
          name="prescriptionId"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <DropdownContainer>
              <FormLabel id={rxLabelId}>Prescription</FormLabel>
              <Dropdown
                labelId={rxLabelId}
                value={field.value}
                onChange={field.onChange}
                disabled={!rxList.length || isFetchingRxList}
              >
                {rxList.map((rx) => (
                  <DropdownItem key={rx.value} value={rx.value}>
                    {rx.description}
                  </DropdownItem>
                ))}
              </Dropdown>
            </DropdownContainer>
          )}
        />
      )}
      <Controller
        name="type"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <DropdownContainer {...createTrackingProps({ unmask: true })}>
            <FormLabel id={taskTypeLabelId}>Task type</FormLabel>
            <Dropdown labelId={taskTypeLabelId} value={field.value || ''} onChange={field.onChange}>
              {basicTaskTypeOptions.map((option: { value: string; label: InboundTaskType }) => (
                <DropdownItem key={option.value} value={option.value}>
                  {option.label}
                </DropdownItem>
              ))}
            </Dropdown>
          </DropdownContainer>
        )}
      />
      <div>
        {/* the CommentAndFileSection is not in a Controller because the refs being passed around conflict with the controller behavior */}
        <div>
          <FormLabel id={taskCommentLabelId}>Description of issue</FormLabel>
          <CommentAndFileSection
            files={newFilesUploaded}
            inputRef={inputRef}
            inputValue={inputValue}
            setInputValue={setInputValue}
            onFilesChange={setNewFilesUploaded}
            hideSubmitButton
            labelId={taskCommentLabelId}
            placeholder=""
          />
        </div>
        <ButtonRow>
          <CancelButton
            {...createTrackingProps({ elementName: 'New Task Form: Cancel Button' })}
            type="button"
            onClick={() => navigate(-1)}
            disabled={submit.isPending}
          >
            Cancel
          </CancelButton>
          <Button
            {...createTrackingProps({ elementName: 'New Task Form: Submit Button' })}
            isLoading={submit.isPending}
            // the inputValue and newFilesUploaded are not tracked by formState.isValid because it's not a regular input field
            disabled={!formState.isValid || (!inputValue && !newFilesUploaded.length)}
            type="submit"
          >
            Submit
          </Button>
        </ButtonRow>
        <ErrorMessage visible={submit.isError}>{submit.error?.response?.data.error}</ErrorMessage>
      </div>
    </FormContainer>
  );
};

export const NewTaskModal: React.FC = () => {
  const navigate = useNavigate();
  const outletContext: any = useOutletContext();
  const authContext = useContext(AuthContext);
  const extended_inbound_task_flow = authContext?.user?.feature_flags['extended_inbound_task_flow'];

  return (
    <Modal onClose={() => navigate(-1)}>
      <ModalTitle>Create a new task</ModalTitle>
      <CustomModalBody>
        {extended_inbound_task_flow ? (
          <ExtendedNewTaskForm
            onSubmit={(taskId: string) => {
              outletContext.reload();
              toast.custom(() => <Toast variant="success">Task created.</Toast>);
              navigate(`/tasks/${taskId}`);
            }}
          />
        ) : (
          <NewTaskForm
            onSubmit={(taskId: string) => {
              outletContext.reload();
              toast.custom(() => <Toast variant="success">Task created.</Toast>);
              navigate(`/tasks/${taskId}`);
            }}
          />
        )}
      </CustomModalBody>
    </Modal>
  );
};

export default NewTaskModal;
